import Entity from '../../../../core/domain/entity';
import UniqueEntityID from '../../../../core/domain/unique_entity_id';
import { PatientId } from '../../../patient/domain/patient';
import { BillId } from '../bill/bill';
import { DietitianId } from '../dietitian';
import { ThirdParty } from '../third_party/third-party';
import { CreditLine } from './credit-line';

export enum CreditStatus {
  EMITTED = 'EMITTED',
  PAID = 'PAID',
}

export interface CreditProps {
  dietitianId: DietitianId;
  patientId: PatientId | undefined;
  thirdParty: ThirdParty | undefined;
  referencePrefix?: string | undefined;
  reference?: number | undefined;
  creditedAt: Date | undefined;
  customerFullname: string | undefined;
  lines: CreditLine[];
  amountExclTax: number;
  amountInclTax: number;
  amountTax: number;
  rateTax: number | undefined;
  withTax: boolean | undefined;
  emittedAt?: Date | undefined;
  paidAt?: Date | undefined;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;
  archived: boolean;
  archivedAt?: Date | undefined;
  status?: CreditStatus | undefined;
  url?: string | undefined;
  path?: string | undefined;
  documentHasReady: boolean;
  billId?: BillId | undefined;
}

export class CreditId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): CreditId {
    return new CreditId(id);
  }
}

export class Credit extends Entity<CreditProps> {
  get creditId(): CreditId {
    return CreditId.create(this.id);
  }

  get dietitianId(): DietitianId | undefined {
    return this.props.dietitianId ?? undefined;
  }

  get patientId(): PatientId | undefined {
    return this.props.patientId;
  }

  set patientId(value: PatientId | undefined) {
    this.props.patientId = value;
  }

  get thirdParty(): ThirdParty | undefined {
    return this.props.thirdParty;
  }

  set thirdParty(value: ThirdParty | undefined) {
    this.props.thirdParty = value;
  }

  get referencePrefix(): string | undefined {
    return this.props.referencePrefix;
  }

  set referencePrefix(value: string | undefined) {
    this.props.referencePrefix = value;
  }

  get reference(): number | undefined {
    return this.props.reference;
  }

  get creditedAt(): Date | undefined {
    return this.props.creditedAt;
  }

  set creditedAt(value: Date | undefined) {
    this.props.creditedAt = value;
  }

  get customerFullname(): string | undefined {
    return this.props.customerFullname;
  }

  set customerFullname(value: string | undefined) {
    this.props.customerFullname = value;
  }

  get lines(): CreditLine[] {
    return this.props.lines;
  }

  get amountExclTax(): number {
    return this.props.amountExclTax;
  }

  get amountInclTax(): number {
    return this.props.amountInclTax;
  }

  get amountTax(): number {
    return this.props.amountTax;
  }

  get rateTax(): number | undefined {
    return this.props.rateTax;
  }

  get withTax(): boolean | undefined {
    return this.props.withTax;
  }

  set withTax(value: boolean | undefined) {
    this.props.withTax = value;
  }

  get emittedAt(): Date | undefined {
    return this.props.emittedAt;
  }

  set emittedAt(value: Date | undefined) {
    this.props.emittedAt = value;
  }

  get paidAt(): Date | undefined {
    return this.props.paidAt;
  }

  set paidAt(value: Date | undefined) {
    this.props.paidAt = value;
  }

  get createdAt(): Date | undefined {
    return this.props.createdAt;
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  get archived(): boolean {
    return this.props.archived;
  }

  get archivedAt(): Date | undefined {
    return this.props.archivedAt;
  }

  get status(): CreditStatus | undefined {
    return this.props.status;
  }

  set status(value: CreditStatus | undefined) {
    this.props.status = value;
  }

  get url(): string | undefined {
    return this.props.url;
  }

  set url(value: string | undefined) {
    this.props.url = value;
  }

  get path(): string | undefined {
    return this.props.path;
  }

  set path(value: string | undefined) {
    this.props.path = value;
  }

  get documentHasReady(): boolean {
    return this.props.documentHasReady;
  }

  set documentHasReady(value: boolean) {
    this.props.documentHasReady = value;
  }

  get billId(): BillId | undefined {
    return this.props.billId;
  }

  private constructor(props: CreditProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: CreditProps): Credit {
    return Credit.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
  }

  archive(): Credit {
    return this.copyWith({ archivedAt: new Date() } as CreditProps);
  }

  restore(): Credit {
    return this.copyWith({ archivedAt: undefined } as CreditProps);
  }

  public static create(props: CreditProps, id?: UniqueEntityID): Credit {
    return new Credit(props, id);
  }
}
